/**
 * Target Lines Utility
 * Calculates target lines and annotations for chart visualizations
 * Matches the logic from the original HTML dashboard
 */

/**
 * Calculate target temperature annotation for alternator temperature charts
 * @param {string} device - Device name (e.g., 'alt1', 'alt2')
 * @param {Array} realTimeData - Current real-time data array
 * @returns {Object|null} Annotation object or null if no target found
 */
export const calculateTempTarget = (device, realTimeData) => {
  if (!device.startsWith('alt') || !Array.isArray(realTimeData)) {
    return null
  }

  // Find target temperature for this alternator
  const targetTempItem = realTimeData.find(
    (item) => item.device === device && item.measurement === 'targetTemp',
  )

  if (!targetTempItem || targetTempItem._value === null || targetTempItem._value === undefined) {
    return null
  }

  const targetValue = targetTempItem._value;  return {
    yaxis: [
      {
        y: targetValue,
        borderColor: '#FF0000', // Red color for temperature target
        strokeDashArray: 4, // Dotted line
        label: {
          borderColor: '#FF0000',
          style: {
            color: '#fff',
            background: '#FF0000',
            fontSize: '14px',
            fontWeight: 'bold',
            padding: {
              left: 3,
              right: 3,
              top: 1,
              bottom: 1,
            },
          },
          text: `Max: ${targetValue}°C`,
          position: 'left', // Position inside chart area on left
          offsetX: 8, // Move inside the chart area
          offsetY: 0,
          textAnchor: 'start',
        },
      },
    ],    yAxisAdjustment: {
      min: targetValue - Math.abs(targetValue) * 0.2,
      max: targetValue + Math.abs(targetValue) * 0.4, // Ensure adequate space above target for label visibility
    },
  }
}

/**
 * Calculate target voltage annotation for voltage charts
 * @param {string} device - Device name (e.g., 'alt1', 'bat1')
 * @param {Array} realTimeData - Current real-time data array
 * @returns {Object|null} Annotation object or null if no target found
 */
export const calculateVoltageTarget = (device, realTimeData) => {
  if (!Array.isArray(realTimeData)) {
    return null
  }

  let targetVoltage = null
  let isCanBusTarget = false

  // Determine device prefix and number
  const devicePrefix = device.startsWith('alt') ? 'alt' : 'bat'
  const deviceNumber = device.replace(devicePrefix, '')
  
  // Find the corresponding battery for this device
  let batteryDevice = `bat${deviceNumber}`
  
  if (device.startsWith('alt')) {
    // For alternators, find which battery they're connected to
    const altData = realTimeData.find(
      (item) => item.device === device && item.measurement === 'batConnected',
    )
    if (altData && altData._value) {
      batteryDevice = `bat${altData._value}`
    }
  }

  // First check if desiredVolts is available from CAN bus
  const desiredVoltsItem = realTimeData.find(
    (item) => item.device === batteryDevice && item.measurement === 'desiredVolts',
  )

  if (desiredVoltsItem && desiredVoltsItem._value !== null && desiredVoltsItem._value !== undefined) {
    // Use desired volts from CAN bus if available
    targetVoltage = desiredVoltsItem._value
    isCanBusTarget = true
  } else {
    // Otherwise determine based on charge stage
    const chargeStageItem = realTimeData.find(
      (item) => item.device === batteryDevice && item.measurement === 'chargeStage',
    )

    if (chargeStageItem && chargeStageItem._value) {
      const chargeStage = chargeStageItem._value
      // Find the corresponding voltage setting based on charge stage
      const voltageItem = realTimeData.find(
        (item) => item.device === batteryDevice && item.measurement === `${chargeStage}.Volts`,
      )

      if (voltageItem && voltageItem._value !== null && voltageItem._value !== undefined) {
        targetVoltage = voltageItem._value
      }
    }
  }

  if (targetVoltage === null) {
    return null
  }

  const color = isCanBusTarget ? '#007bff' : '#FF0000' // Blue for CAN bus, Red for config
  return {
    yaxis: [
      {
        y: targetVoltage,
        borderColor: color,
        strokeDashArray: 4, // Dotted line
        label: {
          borderColor: color,
          style: {
            color: '#fff',
            background: color,
            fontSize: '14px',
            fontWeight: 'bold',
            padding: {
              left: 3,
              right: 3,
              top: 1,
              bottom: 1,
            },
          },
          text: `${isCanBusTarget ? 'CAN bus ' : ''}Target: ${targetVoltage.toFixed(1)}v`,
          position: 'left', // Position inside chart area on left
          offsetX: 8, // Move inside the chart area
          offsetY: 0,
          textAnchor: 'start',
        },
      },
    ],    yAxisAdjustment: {
      min: targetVoltage - Math.abs(targetVoltage) * 0.2,
      max: targetVoltage + Math.abs(targetVoltage) * 0.4, // Ensure adequate space above target for label visibility
    },
  }
}

/**
 * Calculate target amperage annotation for current charts
 * @param {string} device - Device name (e.g., 'alt1', 'bat1')
 * @param {Array} realTimeData - Current real-time data array
 * @returns {Object|null} Annotation object or null if no target found
 */
export const calculateAmpsTarget = (device, realTimeData) => {
  if (!Array.isArray(realTimeData)) {
    return null
  }
  let targetAmps = null
  let isCanBusTarget = false

  // Check if desiredAmps is available directly for this device
  const desiredAmpsItem = realTimeData.find(
    (item) => item.device === device && item.measurement === 'desiredAmps',
  )

  // Check if altCurrentLimit is available for this device (for alternators)
  const altCurrentLimitItem = realTimeData.find(
    (item) => item.device === device && item.measurement === 'altCurrentLimit',
  )

  if (desiredAmpsItem && desiredAmpsItem._value !== null && desiredAmpsItem._value !== undefined) {
    // Use desired amps from CAN bus if available
    targetAmps = desiredAmpsItem._value
    isCanBusTarget = true  } else if (device.startsWith('alt') && altCurrentLimitItem && altCurrentLimitItem._value !== null && altCurrentLimitItem._value !== undefined) {
    // Use alternator current limit if available
    targetAmps = altCurrentLimitItem._value
  } else if (device.startsWith('bat')) {
    // For batteries without desiredAmps, calculate target based on cRate * capacity
    const cRateItem = realTimeData.find(
      (item) => item.device === device && item.measurement === 'cRate',
    )

    const capacityItem = realTimeData.find(
      (item) => item.device === device && item.measurement === 'ampHrCapacity',
    )

    if (
      cRateItem &&
      cRateItem._value !== null &&
      cRateItem._value !== undefined &&
      capacityItem &&
      capacityItem._value !== null &&
      capacityItem._value !== undefined
    ) {
      targetAmps = cRateItem._value * capacityItem._value
    }
  }

  if (targetAmps === null) {
    return null
  }

  const color = isCanBusTarget ? '#007bff' : '#FF0000' // Blue for CAN bus, Red for config
  return {
    yaxis: [
      {
        y: targetAmps,
        borderColor: color,
        strokeDashArray: 4, // Dotted line
        label: {
          borderColor: color,
          style: {
            color: '#fff',
            background: color,
            fontSize: '14px',
            fontWeight: 'bold',
            padding: {
              left: 3,
              right: 3,
              top: 1,
              bottom: 1,
            },
          },
          text: `${isCanBusTarget ? 'CAN bus ' : ''}${device.startsWith('bat') ? 'Target' : 'Max'}: ${Math.round(targetAmps)}A`,
          position: 'left', // Position inside chart area on left
          offsetX: 8, // Move inside the chart area
          offsetY: 0,
          textAnchor: 'start',
        },
      },
    ],    yAxisAdjustment: {
      min: targetAmps - Math.abs(targetAmps) * 0.2,
      max: targetAmps + Math.abs(targetAmps) * 0.4, // Ensure adequate space above target for label visibility
    },
  }
}

/**
 * Get target annotation for any measurement type
 * @param {string} device - Device name
 * @param {string} measurement - Measurement type
 * @param {Array} realTimeData - Current real-time data array
 * @returns {Object|null} Annotation object or null if no target found
 */
export const getTargetAnnotation = (device, measurement, realTimeData) => {
  switch (measurement) {
    case 'temp':
      return calculateTempTarget(device, realTimeData)
    case 'volts':
      return calculateVoltageTarget(device, realTimeData)
    case 'amps':
      return calculateAmpsTarget(device, realTimeData)
    default:
      return null
  }
}
